import type { Texture, Scene } from 'three';

import type { AnimationManager } from '../../../../three-helpers/animation';
import type { ModelManager, TextureManager } from '../../../managers';
import { loadWithKtx2Fallback } from '../../../managers/texture-manager/helpers';

export type UpgradeSkyTextureParams = {
	textureKey: string;
	targetTextureFilename: string;
	fadeDuration: number;
	textureManager: TextureManager;
	animationManager: AnimationManager;
	scene: Scene;
	modelManager: ModelManager | null;
	reflectionSkyKey: string;
	currentSkyKey: string | null;
	setReflectionEnvMap: (envMap: Texture) => void;
	setIsSkyUpgrading: (value: boolean) => void;
};

/**
 * Wait until the browser is idle before proceeding.  While the user is
 * actively dragging / interacting, `requestIdleCallback` will not fire,
 * so heavy work is naturally deferred until they pause.  Falls back to
 * a 200ms setTimeout if `requestIdleCallback` is unavailable.
 */
const waitForIdle = (): Promise<void> => new Promise((resolve) => {
	if (typeof requestIdleCallback === 'function') {
		requestIdleCallback(() => resolve());
	} else {
		setTimeout(resolve, 200);
	}
});

/**
 * Upgrade sky texture to higher resolution with fade transition.
 *
 * Heavy operations (HDR parse, PMREM generation) are isolated with
 * yields between them so the main thread stays responsive for user
 * interaction (camera drag, clicks).
 */
export const upgradeSkyTextureCfn = async (params: UpgradeSkyTextureParams): Promise<void> => {
	const {
		textureKey,
		targetTextureFilename,
		fadeDuration,
		textureManager,
		animationManager,
		scene,
		modelManager,
		reflectionSkyKey,
		currentSkyKey,
		setReflectionEnvMap,
		setIsSkyUpgrading
	} = params;

	const { config } = textureManager;
	if (!config?.basePath || !config?.renderer) {
		setIsSkyUpgrading(false);
		return;
	}

	try {
		const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js');
		const { PMREMGenerator, DataTexture, EquirectangularReflectionMapping, LinearFilter, LinearSRGBColorSpace } = await import('three');

		const texturePath = `${config.basePath}textures/${targetTextureFilename}`;
		const isHdr = /\.hdr($|\?)/i.test(targetTextureFilename);

		let newTexture: Texture;

		if (isHdr) {
			// Step 1: Download the raw HDR data (non-blocking network I/O)
			const response = await fetch(texturePath);
			const buffer = await response.arrayBuffer();

			// Yield so any pending user input is processed
			await waitForIdle();

			// Bail out if the sky changed during download
			if (currentSkyKey !== textureKey) return;

			// Step 2: Parse RGBE data into a DataTexture (CPU-bound)
			const loader = new RGBELoader();
			const parsed = loader.parse(buffer);
			if (!parsed) throw new Error(`Failed to parse HDR: ${targetTextureFilename}`);

			newTexture = new DataTexture(
				parsed.data,
				parsed.width,
				parsed.height,
				(parsed as any).format,
				parsed.type
			);
			newTexture.name = textureKey;
			newTexture.mapping = EquirectangularReflectionMapping;
			// Match what RGBELoader.load() sets on the default-resolution sky this
			// replaces. A bare DataTexture defaults to flipY = false, but the HDR
			// rows run top-down, so without the flip the upgraded sky (and any
			// reflections generated from it) render upside down.
			newTexture.flipY = true;
			newTexture.colorSpace = LinearSRGBColorSpace;
			newTexture.minFilter = LinearFilter;
			newTexture.magFilter = LinearFilter;
			newTexture.generateMipmaps = false;
			newTexture.needsUpdate = true;
		} else {
			// For non-HDR textures, try KTX2 variant first, fall back to original
			newTexture = await new Promise<Texture>((resolve, reject) => {
				loadWithKtx2Fallback({
					originalUrl: texturePath,
					ktx2Support: textureManager.ktx2Support,
					textureLoader: textureManager.textureLoader,
					hdrLoader: textureManager.hdrLoader,
					onLoad: (texture) => {
						texture.name = textureKey;
						texture.mapping = EquirectangularReflectionMapping;
						resolve(texture);
					},
					onError: (error) => {
						console.error('Error loading high-res sky texture:', textureKey, error);
						reject(error);
					}
				});
			});
		}

		// Yield before expensive PMREM generation
		await waitForIdle();

		// Bail out if the sky changed while we were parsing
		if (currentSkyKey !== textureKey) {
			newTexture.dispose();
			return;
		}

		// Step 3: Generate PMREM environment map (GPU-bound)
		const preloader = textureManager.pmremPreloader;
		let envMap: Texture;
		const upgradeKey = `${textureKey}-upgrade`;

		if (preloader?.isInitialized) {
			const result = preloader.generatePmremSync(upgradeKey, newTexture);
			if (result) {
				envMap = result;
			} else {
				const pmremGenerator = new PMREMGenerator(config.renderer);
				pmremGenerator.compileEquirectangularShader();
				envMap = pmremGenerator.fromEquirectangular(newTexture).texture;
				pmremGenerator.dispose();
			}
		} else {
			const pmremGenerator = new PMREMGenerator(config.renderer);
			pmremGenerator.compileEquirectangularShader();
			envMap = pmremGenerator.fromEquirectangular(newTexture).texture;
			pmremGenerator.dispose();
		}

		// Yield before animations so the render loop can process frames
		await waitForIdle();

		// Cross-fade: dim briefly, swap textures, then restore
		const dimTarget = 0.7;
		const dimDuration = Math.round(fadeDuration * 0.4);
		const brightenDuration = Math.round(fadeDuration * 0.6);

		// Phase 1: Dim
		await animationManager.animateOpacity(
			'sky-upgrade-dim',
			1,
			dimTarget,
			dimDuration,
			(intensity) => {
				scene.backgroundIntensity = intensity;
			}
		);

		// Background must be the raw equirectangular texture — assigning the
		// PMREM-generated envMap here makes Three.js render the background via
		// the CubeUV path, which projects with a different vertical orientation
		// than EquirectangularReflectionMapping and shows the sky upside-down.
		// envMap is for scene.environment (PBR reflections) only.
		scene.background = newTexture;

		// Update texture manager cache
		(textureManager as any)._textures?.set(textureKey, newTexture);

		// If upgrading the reflection sky itself, also update environment
		if (textureKey === reflectionSkyKey) {
			scene.environment = envMap;
			setReflectionEnvMap(envMap);
			(textureManager as any)._environmentMap = envMap;
			if (modelManager) {
				modelManager.setEnvironmentMap(envMap);
			}
		}

		// Phase 2: Fade in new background
		await animationManager.animateOpacity(
			'sky-upgrade-brighten',
			dimTarget,
			1,
			brightenDuration,
			(intensity) => {
				scene.backgroundIntensity = intensity;
			}
		);
	} finally {
		setIsSkyUpgrading(false);
	}
};
